import { useCallback, useEffect, useState } from "react";
import api from "../../../../services/api";
import {
  createCardFromSchedule,
  markScheduleItemAsHavingCard,
  searchSchedules,
  type ScheduleSearchItem,
  type ScheduleSearchMeta,
} from "../../../../services/scheduleCard";
import { useAuth } from "../../../../hooks/auth";
import { useToast } from "../../../../hooks/toast";
import refreshToken from "../../../../utils/refreshToken";
import {
  buildKanbanScheduleSearchInput,
  isDateRangeValid,
} from "../../../../utils/scheduleSearchParams";

export type ScheduleSearchMode = "client" | "date";

const EMPTY_META: ScheduleSearchMeta = {
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0,
};

export function useCreateCardFromSchedule(options: {
  isOpen: boolean;
  onCreated: () => Promise<void> | void;
}) {
  const { isOpen, onCreated } = options;
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [mode, setMode] = useState<ScheduleSearchMode>("client");
  const [clientQuery, setClientQuery] = useState("");
  const [debouncedClientQuery, setDebouncedClientQuery] = useState("");
  const [date, setDate] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ScheduleSearchItem[]>([]);
  const [meta, setMeta] = useState<ScheduleSearchMeta>(EMPTY_META);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [pendingScheduleId, setPendingScheduleId] = useState<number | null>(null);
  const [creatingScheduleId, setCreatingScheduleId] = useState<number | null>(null);

  const rangeError =
    mode === "date" && date && dateTo && !isDateRangeValid(date, dateTo)
      ? "A data final deve ser igual ou posterior à data inicial."
      : "";

  const canSearch =
    mode === "client"
      ? Boolean(debouncedClientQuery.trim())
      : Boolean(date) && !rangeError;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedClientQuery(clientQuery.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timeout);
  }, [clientQuery]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasSearched(false);
    setPendingScheduleId(null);
  }, [mode]);

  useEffect(() => {
    if (canSearch) return;
    setItems([]);
    setHasSearched(false);
    setPendingScheduleId(null);
  }, [canSearch]);

  const authorize = useCallback(async () => {
    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token || access_token}`;
  }, [access_token, dateToExpires]);

  useEffect(() => {
    if (!isOpen || !canSearch) return;

    let cancelled = false;

    async function runSearch() {
      setIsSearching(true);
      try {
        await authorize();
        const result = await searchSchedules(
          api,
          buildKanbanScheduleSearchInput({
            mode,
            search: debouncedClientQuery,
            date,
            dateTo,
            page,
          })
        );
        if (cancelled) return;
        setItems(result.items);
        setMeta(result.meta);
        setHasSearched(true);
      } catch {
        if (cancelled) return;
        setItems([]);
        setMeta(EMPTY_META);
        setHasSearched(true);
        addToast({
          type: "error",
          title: "Erro ao buscar agendamentos",
          description: "Tente novamente com outros filtros.",
        });
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [
    addToast,
    authorize,
    canSearch,
    date,
    dateTo,
    debouncedClientQuery,
    isOpen,
    mode,
    page,
  ]);

  const createCard = useCallback(
    async (scheduleId: number) => {
      setCreatingScheduleId(scheduleId);
      try {
        await authorize();
        const result = await createCardFromSchedule(api, scheduleId);

        if (result.ok) {
          addToast({
            type: "success",
            title: result.message,
            description: result.card.description
              ? `Card "${result.card.description}" criado no Kanban, Synkronos e BPM.`
              : "Card criado no Kanban, Synkronos e BPM.",
          });
          void onCreated();
          return true;
        }

        if (result.reason === "already_has_card") {
          setItems((current) =>
            markScheduleItemAsHavingCard(current, scheduleId, result.cardId)
          );
          setPendingScheduleId(null);
          addToast({ type: "info", title: result.message });
          return false;
        }

        addToast({ type: "error", title: result.message });
        return false;
      } finally {
        setCreatingScheduleId(null);
      }
    },
    [addToast, authorize, onCreated]
  );

  return {
    mode,
    setMode,
    clientQuery,
    setClientQuery,
    date,
    setDate,
    dateTo,
    setDateTo,
    page,
    setPage,
    items,
    meta,
    hasSearched,
    isSearching,
    rangeError,
    pendingScheduleId,
    setPendingScheduleId,
    creatingScheduleId,
    createCard,
  };
}
